import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Plus, Play, Pause, Trash2, Edit3, Clock, Calendar, DollarSign, Users, FileText, AlertCircle, CheckCircle, X } from 'lucide-react';
import { recurringAPI, customersAPI, vendorsAPI, employeesAPI, productsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { fmtMoney } from '../../utils/currency';

const fmt = fmtMoney;

const TEMPLATE_TYPES = [
  { value: 'Invoice', label: 'Recurring Invoice', icon: '📄', color: '#2563eb' },
  { value: 'Bill', label: 'Recurring Bill', icon: '🧾', color: '#7c3aed' },
  { value: 'Payroll', label: 'Recurring Payroll', icon: '💰', color: '#16a34a' },
];

const FREQUENCIES = [
  { value: 'Daily', label: 'Daily' },
  { value: 'Weekly', label: 'Weekly' },
  { value: 'BiWeekly', label: 'Bi-Weekly' },
  { value: 'Monthly', label: 'Monthly' },
  { value: 'Quarterly', label: 'Quarterly' },
  { value: 'Yearly', label: 'Yearly' },
];

function emptyTemplateData(type) {
  if (type === 'Invoice') {
    return { customer_id: '', items: [{ description: '', quantity: 1, unit_price: 0, tax_rate: 0 }], payment_terms: 'Net 30', memo: '' };
  }
  if (type === 'Bill') {
    return { vendor_id: '', items: [{ description: '', quantity: 1, unit_price: 0, tax_rate: 0 }], memo: '' };
  }
  if (type === 'Payroll') {
    return { employee_ids: [], memo: 'Recurring payroll run' };
  }
  return {};
}

export default function RecurringSchedules() {
  const navigate = useNavigate();
  const [schedules, setSchedules] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [generating, setGenerating] = useState(null);

  // Form state
  const [form, setForm] = useState({
    template_type: 'Invoice',
    template_name: '',
    frequency: 'Monthly',
    next_date: new Date().toISOString().split('T')[0],
    end_date: '',
    is_active: true,
    template_data: emptyTemplateData('Invoice'),
  });

  // Related data
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [products, setProducts] = useState([]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [s, st] = await Promise.all([recurringAPI.list(), recurringAPI.stats()]);
      setSchedules(s);
      setStats(st);
      const [c, v, e, p] = await Promise.all([
        customersAPI.list({}), vendorsAPI.list({}), employeesAPI.list(), productsAPI.list({})
      ]);
      setCustomers(c);
      setVendors(v);
      setEmployees(e);
      setProducts(p);
    } catch (err) { toast.error(err.message); }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const filtered = useMemo(() => {
    if (!filter) return schedules;
    return schedules.filter(s => s.template_type === filter);
  }, [schedules, filter]);

  const openCreate = (type = 'Invoice') => {
    setEditItem(null);
    setForm({
      template_type: type,
      template_name: '',
      frequency: 'Monthly',
      next_date: new Date().toISOString().split('T')[0],
      end_date: '',
      is_active: true,
      template_data: emptyTemplateData(type),
    });
    setShowModal(true);
  };

  const openEdit = (item) => {
    const data = typeof item.template_data === 'string' ? JSON.parse(item.template_data) : (item.template_data || {});
    setEditItem(item);
    setForm({
      template_type: item.template_type,
      template_name: item.template_name,
      frequency: item.frequency,
      next_date: item.next_date?.split('T')[0] || '',
      end_date: item.end_date?.split('T')[0] || '',
      is_active: !!item.is_active,
      template_data: { ...emptyTemplateData(item.template_type), ...data },
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.template_name.trim()) { toast.error('Template name is required'); return; }
    try {
      if (editItem) {
        await recurringAPI.update(editItem.id, form);
        toast.success('Schedule updated');
      } else {
        await recurringAPI.create(form);
        toast.success('Schedule created');
      }
      setShowModal(false);
      loadData();
    } catch (err) { toast.error(err.message); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete schedule "${item.template_name}"?`)) return;
    try {
      await recurringAPI.remove(item.id);
      toast.success('Schedule deleted');
      loadData();
    } catch (err) { toast.error(err.message); }
  };

  const handleGenerate = async (item) => {
    setGenerating(item.id);
    try {
      const res = await recurringAPI.generate(item.id);
      toast.success(res.message || 'Generated successfully');
      loadData();
    } catch (err) { toast.error(err.message); }
    setGenerating(null);
  };

  const handleRunAllDue = async () => {
    try {
      const res = await recurringAPI.runDue();
      toast.success(`Processed ${res.processed} schedule(s)`);
      loadData();
    } catch (err) { toast.error(err.message); }
  };

  const toggleActive = async (item) => {
    try {
      const data = typeof item.template_data === 'string' ? JSON.parse(item.template_data) : (item.template_data || {});
      await recurringAPI.update(item.id, {
        ...item,
        template_data: data,
        is_active: !item.is_active,
      });
      toast.success(item.is_active ? 'Paused' : 'Resumed');
      loadData();
    } catch (err) { toast.error(err.message); }
  };

  // Calculate days until next run
  const daysUntil = (dateStr) => {
    if (!dateStr) return null;
    const diff = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
    return diff;
  };

  const getStatusColor = (item) => {
    if (!item.is_active) return { bg: '#f3f4f6', color: '#6b7280', label: 'Paused' };
    const days = daysUntil(item.next_date);
    if (days < 0) return { bg: '#fef2f2', color: '#dc2626', label: `${Math.abs(days)}d overdue` };
    if (days === 0) return { bg: '#fef3c7', color: '#d97706', label: 'Due today' };
    if (days <= 3) return { bg: '#fef3c7', color: '#d97706', label: `In ${days}d` };
    return { bg: '#dcfce7', color: '#16a34a', label: `In ${days}d` };
  };

  // Item row helpers
  const addLineItem = () => {
    setForm(f => ({
      ...f,
      template_data: { ...f.template_data, items: [...(f.template_data.items || []), { description: '', quantity: 1, unit_price: 0, tax_rate: 0 }] },
    }));
  };
  const removeLineItem = (idx) => {
    setForm(f => ({
      ...f,
      template_data: { ...f.template_data, items: (f.template_data.items || []).filter((_, i) => i !== idx) },
    }));
  };
  const updateLineItem = (idx, field, value) => {
    setForm(f => ({
      ...f,
      template_data: {
        ...f.template_data,
        items: (f.template_data.items || []).map((item, i) => i === idx ? { ...item, [field]: value } : item),
      },
    }));
  };

  return (
    <div>
      <div className="toolbar">
        <h2>🔄 Recurring Schedules</h2>
        <div className="toolbar-right">
          <button className="btn btn-outline" onClick={handleRunAllDue} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Play size={14} /> Run All Due
          </button>
          <button className="btn btn-primary" onClick={() => openCreate('Invoice')} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Plus size={14} /> New Schedule
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="stats-grid" style={{ marginBottom: 16 }}>
          <div className="stat-card primary">
            <div className="stat-label">Total Schedules</div>
            <div className="stat-value">{stats.total}</div>
          </div>
          <div className="stat-card success">
            <div className="stat-label">Active</div>
            <div className="stat-value">{stats.active}</div>
          </div>
          <div className="stat-card warning">
            <div className="stat-label">Due This Week</div>
            <div className="stat-value">{stats.upcoming}</div>
          </div>
          <div className="stat-card danger">
            <div className="stat-label">Overdue</div>
            <div className="stat-value">{stats.overdue}</div>
          </div>
        </div>
      )}

      {/* Quick Create Buttons */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {TEMPLATE_TYPES.map(t => (
          <button key={t.value} className="btn btn-outline" onClick={() => openCreate(t.value)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <span style={{ fontSize: 16 }}>{t.icon}</span> {t.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="filters-bar" style={{ marginBottom: 12 }}>
        {['', 'Invoice', 'Bill', 'Payroll'].map(f => (
          <button key={f} className={`btn btn-sm ${filter === f ? 'btn-primary' : 'btn-outline'}`} onClick={() => setFilter(f)}>
            {f || 'All'}
          </button>
        ))}
      </div>

      {/* Schedule List */}
      {loading ? (
        <div className="spinner" style={{ margin: '40px auto' }} />
      ) : filtered.length === 0 ? (
        <div className="card">
          <div className="card-body" style={{ padding: 40, textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🔄</div>
            <h3 style={{ color: '#64748b' }}>No recurring schedules</h3>
            <p style={{ color: '#94a3b8', fontSize: 12 }}>Create a recurring schedule to automate invoices, bills, or payroll.</p>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-body" style={{ padding: 0 }}>
            <table>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Template Name</th>
                  <th>Frequency</th>
                  <th>Next Run</th>
                  <th>Status</th>
                  <th>Last Generated</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(item => {
                  const status = getStatusColor(item);
                  const typeInfo = TEMPLATE_TYPES.find(t => t.value === item.template_type) || {};
                  const data = typeof item.template_data === 'string' ? JSON.parse(item.template_data) : (item.template_data || {});
                  return (
                    <tr key={item.id}>
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <span style={{ fontSize: 16 }}>{typeInfo.icon}</span>
                          <span style={{ fontWeight: 600, color: typeInfo.color }}>{item.template_type}</span>
                        </span>
                      </td>
                      <td style={{ fontWeight: 600 }}>{item.template_name}</td>
                      <td>{item.frequency}</td>
                      <td>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Calendar size={12} style={{ color: '#94a3b8' }} />
                          {item.next_date ? new Date(item.next_date).toLocaleDateString() : '—'}
                        </span>
                      </td>
                      <td>
                        <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: status.bg, color: status.color }}>
                          {status.label}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: '#64748b' }}>
                        {item.last_generated_date
                          ? new Date(item.last_generated_date).toLocaleDateString()
                          : 'Never'}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                          <button className="btn btn-sm btn-outline" title="Generate Now"
                            onClick={() => handleGenerate(item)} disabled={generating === item.id || !item.is_active}
                            style={{ padding: '3px 8px' }}>
                            {generating === item.id ? <RefreshCw size={12} className="spin" /> : <Play size={12} />}
                          </button>
                          <button className="btn btn-sm btn-outline" title={item.is_active ? 'Pause' : 'Resume'}
                            onClick={() => toggleActive(item)}
                            style={{ padding: '3px 8px' }}>
                            {item.is_active ? <Pause size={12} /> : <Play size={12} />}
                          </button>
                          <button className="btn btn-sm btn-outline" title="Edit"
                            onClick={() => openEdit(item)}
                            style={{ padding: '3px 8px' }}>
                            <Edit3 size={12} />
                          </button>
                          <button className="btn btn-sm btn-danger" title="Delete"
                            onClick={() => handleDelete(item)}
                            style={{ padding: '3px 8px', background: '#fff', border: '1px solid #dc2626', color: '#dc2626' }}>
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Generations */}
      {stats?.recentGenerations?.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-header"><h3>Recent Generations</h3></div>
          <div className="card-body" style={{ padding: 0 }}>
            <table>
              <thead><tr><th>Template</th><th>Type</th><th>Date</th><th>Generated ID</th></tr></thead>
              <tbody>
                {stats.recentGenerations.map((r, i) => (
                  <tr key={i}>
                    <td>{r.template_name}</td>
                    <td>{r.template_type}</td>
                    <td>{r.last_generated_date ? new Date(r.last_generated_date).toLocaleDateString() : '—'}</td>
                    <td className="font-mono">{r.last_generated_id || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && createPortal(
        <div className="modal-overlay" onClick={() => setShowModal(false)} style={{ zIndex: 1100 }}>
          <div className="modal" style={{ maxWidth: 640, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editItem ? 'Edit Schedule' : 'New Recurring Schedule'}</h3>
              <button className="btn-ghost" onClick={() => setShowModal(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              {/* Type */}
              <div className="form-group">
                <label>Type <span style={{ color: '#dc2626' }}>*</span></label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {TEMPLATE_TYPES.map(t => (
                    <button key={t.value} type="button" className="btn" onClick={() => {
                      setForm(f => ({ ...f, template_type: t.value, template_data: emptyTemplateData(t.value) }));
                    }} style={{
                      flex: 1, padding: '10px 12px', border: `2px solid ${form.template_type === t.value ? t.color : '#e5e7eb'}`,
                      background: form.template_type === t.value ? t.color + '10' : '#fff',
                      color: form.template_type === t.value ? t.color : '#374151',
                      fontWeight: 600, fontSize: 12, borderRadius: 6, cursor: 'pointer',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                    }}>
                      <span style={{ fontSize: 20 }}>{t.icon}</span>
                      <span>{t.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Name */}
              <div className="form-group">
                <label>Schedule Name <span style={{ color: '#dc2626' }}>*</span></label>
                <input className="form-control" value={form.template_name} onChange={e => setForm(f => ({ ...f, template_name: e.target.value }))} placeholder="e.g. Monthly Invoice for Acme Corp" />
              </div>

              {/* Frequency + Dates */}
              <div className="form-grid">
                <div className="form-group">
                  <label>Frequency</label>
                  <select className="form-control" value={form.frequency} onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))}>
                    {FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Start Date (Next Run)</label>
                  <input className="form-control" type="date" value={form.next_date} onChange={e => setForm(f => ({ ...f, next_date: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>End Date (Optional)</label>
                  <input className="form-control" type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
                  <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>Leave empty for no end</div>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select className="form-control" value={form.is_active ? 'active' : 'paused'} onChange={e => setForm(f => ({ ...f, is_active: e.target.value === 'active' }))}>
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                  </select>
                </div>
              </div>

              {/* Type-specific content */}
              {form.template_type === 'Invoice' && (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 14, marginTop: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: 10 }}>Invoice Details</div>
                  <div className="form-group">
                    <label>Customer</label>
                    <select className="form-control" value={form.template_data.customer_id || ''} onChange={e => setForm(f => ({ ...f, template_data: { ...f.template_data, customer_id: e.target.value } }))}>
                      <option value="">Select customer...</option>
                      {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Payment Terms</label>
                    <select className="form-control" value={form.template_data.payment_terms || 'Net 30'} onChange={e => setForm(f => ({ ...f, template_data: { ...f.template_data, payment_terms: e.target.value } }))}>
                      {['Due on Receipt', 'Net 15', 'Net 30', 'Net 45', 'Net 60'].map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <LineItemsEditor items={form.template_data.items || []} products={products} onAdd={addLineItem} onRemove={removeLineItem} onUpdate={updateLineItem} />
                  <div className="form-group">
                    <label>Memo / Notes</label>
                    <input className="form-control" value={form.template_data.memo || ''} onChange={e => setForm(f => ({ ...f, template_data: { ...f.template_data, memo: e.target.value } }))} placeholder="Internal memo..." />
                  </div>
                </div>
              )}

              {form.template_type === 'Bill' && (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 14, marginTop: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: 10 }}>Bill Details</div>
                  <div className="form-group">
                    <label>Vendor</label>
                    <select className="form-control" value={form.template_data.vendor_id || ''} onChange={e => setForm(f => ({ ...f, template_data: { ...f.template_data, vendor_id: e.target.value } }))}>
                      <option value="">Select vendor...</option>
                      {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                    </select>
                  </div>
                  <LineItemsEditor items={form.template_data.items || []} products={products} onAdd={addLineItem} onRemove={removeLineItem} onUpdate={updateLineItem} />
                  <div className="form-group">
                    <label>Memo / Notes</label>
                    <input className="form-control" value={form.template_data.memo || ''} onChange={e => setForm(f => ({ ...f, template_data: { ...f.template_data, memo: e.target.value } }))} placeholder="Internal memo..." />
                  </div>
                </div>
              )}

              {form.template_type === 'Payroll' && (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 14, marginTop: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: 10 }}>Payroll Details</div>
                  <div className="form-group">
                    <label>Select Employees</label>
                    <div style={{ border: '1px solid #d1d5db', borderRadius: 4, maxHeight: 200, overflow: 'auto', padding: 8 }}>
                      {employees.filter(e => e.is_active).map(emp => {
                        const checked = (form.template_data.employee_ids || []).includes(emp.id);
                        return (
                          <label key={emp.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', fontSize: 12 }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => {
                                const ids = form.template_data.employee_ids || [];
                                const newIds = checked ? ids.filter(id => id !== emp.id) : [...ids, emp.id];
                                setForm(f => ({ ...f, template_data: { ...f.template_data, employee_ids: newIds } }));
                              }}
                            />
                            <span style={{ fontWeight: 500 }}>{emp.first_name} {emp.last_name}</span>
                            <span style={{ color: '#94a3b8', fontSize: 11 }}>({emp.employee_number}) — {emp.job_title || 'Employee'}</span>
                          </label>
                        );
                      })}
                      {employees.filter(e => e.is_active).length === 0 && (
                        <div style={{ padding: 12, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>No active employees</div>
                      )}
                    </div>
                    <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>
                      {(form.template_data.employee_ids || []).length} employee(s) selected
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Notes</label>
                    <input className="form-control" value={form.template_data.memo || ''} onChange={e => setForm(f => ({ ...f, template_data: { ...f.template_data, memo: e.target.value } }))} placeholder="Payroll notes..." />
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave}>
                {editItem ? 'Update Schedule' : 'Create Schedule'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// Line Items sub-component
function LineItemsEditor({ items, products, onAdd, onRemove, onUpdate }) {
  return (
    <div className="form-group">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <label style={{ margin: 0 }}>Line Items</label>
        <button type="button" className="btn btn-sm btn-outline" onClick={onAdd} style={{ fontSize: 10, padding: '2px 8px' }}>+ Add</button>
      </div>
      {items.map((item, idx) => (
        <div key={idx} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
          <select style={{ flex: 3, padding: '4px 6px', fontSize: 11, border: '1px solid #d1d5db', borderRadius: 3 }}
            value={item.product_id || ''} onChange={e => {
              const p = products.find(pr => pr.id === parseInt(e.target.value));
              onUpdate(idx, 'product_id', e.target.value);
              if (p) {
                onUpdate(idx, 'description', p.name);
                onUpdate(idx, 'unit_price', parseFloat(p.sales_price) || 0);
              }
            }}>
            <option value="">Select product...</option>
            {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <input style={{ flex: 2, padding: '4px 6px', fontSize: 11, border: '1px solid #d1d5db', borderRadius: 3 }}
            placeholder="Description" value={item.description || ''} onChange={e => onUpdate(idx, 'description', e.target.value)} />
          <input type="number" min="0" step="1" style={{ width: 50, padding: '4px 4px', fontSize: 11, border: '1px solid #d1d5db', borderRadius: 3, textAlign: 'right' }}
            value={item.quantity || 1} onChange={e => onUpdate(idx, 'quantity', parseFloat(e.target.value) || 0)} />
          <input type="number" min="0" step="0.01" style={{ width: 80, padding: '4px 4px', fontSize: 11, border: '1px solid #d1d5db', borderRadius: 3, textAlign: 'right' }}
            value={item.unit_price || 0} onChange={e => onUpdate(idx, 'unit_price', parseFloat(e.target.value) || 0)} />
          <button type="button" onClick={() => onRemove(idx)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: 2 }}>
            <Trash2 size={12} />
          </button>
        </div>
      ))}
      {items.length === 0 && (
        <div style={{ padding: 10, textAlign: 'center', color: '#94a3b8', fontSize: 11, border: '1px dashed #d1d5db', borderRadius: 4 }}>
          No line items. Click "+ Add" to add one.
        </div>
      )}
    </div>
  );
}
